import * as React from "react";

import { useAnalytics } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";
import type { EventModel, OrgRecord } from "@/shared/domain/orgs";
import {
  Banner,
  Button,
  ConfirmDialog,
  DropdownMenu,
} from "@/shared/foundation/primitives";
import { userFacingErrorMessage } from "@/shared/http/userFacingErrorMessage";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

/** Event models in order. An org moves only forward through them, possibly skipping some. */
const EVENT_MODEL_ORDER: readonly EventModel[] = [
  "legacy",
  "dual",
  "migrating",
  "session",
];

const EVENT_MODEL_LABELS: Record<EventModel, string> = {
  legacy: "Legacy",
  dual: "Dual",
  migrating: "Migrating",
  session: "Session",
};

/** What the org's events look like once it is on each model. */
const EVENT_MODEL_EFFECTS: Record<EventModel, string> = {
  legacy: "The org has dataset events only.",
  dual: "The org has both dataset events and session events.",
  migrating:
    "The org's dataset events become read-only, and new events are session events.",
  session:
    "The org has session events only, and its dataset events are no longer available.",
};

interface OrgEventModelButtonProps {
  orgId: string;
  currentEventModel: EventModel;
  onSuccess?: (updatedOrg: OrgRecord) => void;
}

/**
 * Roboto-admin control for moving an org forward through the event models.
 * Render inside a `SystemUserOnly` gate; the button does no gating itself.
 *
 * The menu lists only the models after the current one, because the server
 * refuses to move back. Picking one asks for confirmation, since the move
 * cannot be undone.
 */
export const OrgEventModelButton: React.FC<OrgEventModelButtonProps> = ({
  orgId,
  currentEventModel,
  onSuccess,
}) => {
  const { orgService } = useDomainServices();
  const { trackEvent } = useAnalytics();
  const [target, setTarget] = React.useState<EventModel | null>(null);
  const [isUpdating, setIsUpdating] = React.useState(false);
  const [error, setError] = React.useState<React.ReactNode>(null);

  const laterModels = EVENT_MODEL_ORDER.slice(
    EVENT_MODEL_ORDER.indexOf(currentEventModel) + 1,
  );
  const isFinal = laterModels.length === 0;
  const currentLabel = EVENT_MODEL_LABELS[currentEventModel];

  function closeConfirmation() {
    setTarget(null);
    setError(null);
  }

  async function updateEventModel(eventModel: EventModel) {
    setIsUpdating(true);
    setError(null);
    try {
      const updatedOrg = await orgService.updateOrgEventModel(
        orgId,
        eventModel,
      );
      setTarget(null);
      onSuccess?.(updatedOrg);
    } catch (e: unknown) {
      ErrorMonitoringService.captureError(e);
      setError(userFacingErrorMessage(e));
    } finally {
      setIsUpdating(false);
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenu.Trigger
          render={
            <Button
              onClick={() =>
                trackEvent("OrgEventModelClicked", {
                  orgId,
                  eventModel: currentEventModel,
                })
              }
              variant="outlined"
              disabled={isFinal}
              title={
                isFinal
                  ? "This org is already on the session event model, the last one."
                  : undefined
              }
            >
              {`Event model: ${currentLabel}`}
            </Button>
          }
        />
        <DropdownMenu.Content>
          {laterModels.map((model) => (
            <DropdownMenu.Item key={model} onClick={() => setTarget(model)}>
              {`Move to ${EVENT_MODEL_LABELS[model]}`}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu>
      {target !== null && (
        <ConfirmDialog
          open
          onOpenChange={(open) => {
            if (!open) {
              closeConfirmation();
            }
          }}
          title={`Move this org from ${currentLabel} to ${EVENT_MODEL_LABELS[target]}?`}
          description={`${EVENT_MODEL_EFFECTS[target]} This cannot be undone.`}
          confirmLabel={`Move to ${EVENT_MODEL_LABELS[target]}`}
          destructive
          pending={isUpdating}
          onConfirm={() => void updateEventModel(target)}
        >
          {error !== null && (
            <Banner variant="error" role="alert">
              {error}
            </Banner>
          )}
        </ConfirmDialog>
      )}
    </>
  );
};
